import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/staffApi";
import { screenMessage } from "@/lib/screenMessage";

/* Approval-based refunds. Members ask with "Request refund"; an admin approves or rejects in the console.
   Amounts are minor units (paise/cents) from the server; the client never sends a cost or price. */

const money = (minor, cur = "INR") => {
  try { return new Intl.NumberFormat("en-IN", { style: "currency", currency: (cur || "INR").toUpperCase() }).format((minor || 0) / 100); }
  catch { return `${(minor || 0) / 100} ${cur}`; }
};
const err = (e, d) => {
  const x = e?.response?.data?.detail;
  if (typeof x === "string") return x;
  if (x && typeof x.message === "string") return x.message;
  return d;
};
const STATUS = { pending: "Under review", processing: "Being processed", approved: "Refunded", rejected: "Declined", refund_failed: "Delayed, we're retrying" };

export function RequestRefundButton({ kind, refId, onDone, className = "" }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const ask = async () => {
    const reason = window.prompt("Request refund: tell us why (optional). An admin reviews every request; nothing is refunded until it's approved.", "");
    if (reason === null) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/refund-requests", { kind, ref_id: refId, reason });
      toast.success(`Refund requested. Suggested amount ${money(data.suggested_minor, data.currency)}; an admin will review it.`);
      onDone && onDone();
    } catch (e) { const m = err(e, "Couldn't request a refund. Please try again."); setError(m); toast.error(m); }
    finally { setBusy(false); }
  };
  return (
    <span className="inline-flex flex-col">
      <button type="button" disabled={busy} onClick={ask} data-testid={`request-refund-${refId}`}
        className={className || "px-3 py-1.5 rounded-md border text-xs uppercase tracking-widest"}>{busy ? "Sending…" : "Request refund"}</button>
      {error && <span role="alert" data-testid={`request-refund-error-${refId}`} className="text-[11px]">{error} Tap Request refund to try again.</span>}
    </span>
  );
}

export function MyRefundRequests({ reloadKey }) {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);
  useEffect(() => {
    setState("loading");
    api.get("/refund-requests/mine")
      .then(({ data }) => { setRows(data || []); setState("ready"); })
      .catch((e) => { setError(err(e, "Couldn't load your refund requests.")); setState("error"); });
  }, [reloadKey, tick]);
  if (state === "loading") return <p data-testid="refund-requests-loading" className="mt-4 text-xs">Loading refund requests…</p>;
  if (state === "error") return (
    <p data-testid="refund-requests-error" className="mt-4 text-xs">{error}{" "}
      <button type="button" data-testid="refund-requests-retry" onClick={() => setTick((t) => t + 1)} className="underline">Retry</button></p>
  );
  if (!rows.length) return null;
  return (
    <div data-testid="my-refund-requests" className="mt-4 text-xs">
      <p className="font-semibold">Refund requests</p>
      {rows.map((r) => (
        <p key={r.id} data-testid={`refund-request-${r.id}`} className="mt-1">
          {r.label || r.kind} · {STATUS[r.status] || r.status}
          {r.status === "approved" && r.approved_minor ? ` · ${money(r.approved_minor, r.currency)}` : ""}
          {r.status === "rejected" && r.reject_reason ? ` · Reason: ${r.reject_reason}` : ""}
        </p>
      ))}
    </div>
  );
}

export function AdminRefundQueue() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/refund-requests", { params: { status } }).then(({ data }) => setRows(data || [])).catch(() => toast.error(screenMessage("admin")));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  const approve = async (r) => {
    const v = window.prompt(`Approve refund. Suggested ${money(r.suggested_minor, r.currency)} (rechecked at approval). Amount in ${(r.currency || "INR").toUpperCase()}, or leave blank for the full suggested amount:`, "");
    if (v === null) return;
    const body = {};
    if (v.trim()) { const n = Math.round(parseFloat(v) * 100); if (!(n > 0)) { toast.error("Enter a positive amount"); return; } body.amount_minor = n; }
    try { await api.post(`/admin/refund-requests/${r.id}/approve`, body); toast.success("Plan cancelled and refund sent"); load(); }
    catch { toast.error(screenMessage("admin")); load(); }
  };
  const reject = async (r) => {
    const reason = window.prompt("Reject refund: the member will see this reason", "");
    if (!reason || reason.trim().length < 3) return;
    try { await api.post(`/admin/refund-requests/${r.id}/reject`, { reason }); toast.success("Rejected"); load(); }
    catch { toast.error(screenMessage("reset")); }
  };
  const markPaid = async (r) => {
    const reference = window.prompt("Mark paid: you refunded this by hand. Enter the payment reference (UTR, bank or dashboard refund ID):", "");
    if (!reference || reference.trim().length < 3) return;
    try { await api.post(`/admin/refund-requests/${r.id}/mark-paid`, { reference: reference.trim() }); toast.success("Marked paid"); load(); }
    catch { toast.error(screenMessage("admin")); load(); }
  };
  return (
    <section data-testid="admin-refund-queue" className="mt-6">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold">Refund requests</h3>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="refund-queue-filter" className="text-xs border rounded px-1">
          {["pending", "refund_failed", "processing", "approved", "rejected", "all"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {!rows.length && <p className="text-xs mt-2">No requests.</p>}
      {rows.map((r) => (
        <div key={r.id} data-testid={`admin-refund-${r.id}`} className="mt-2 text-xs border rounded p-2">
          <p>{r.label || r.kind} · user {r.user_id} · {r.status}{r.fail_stage ? ` (${r.fail_stage})` : ""} · suggested {money(r.suggested_minor, r.currency)}</p>
          {r.reason && <p>Reason: {r.reason}</p>}
          {r.breakdown && r.breakdown.ai_deduction_minor > 0 && <p>AI usage deduction: {money(r.breakdown.ai_deduction_minor, r.currency)}</p>}
          {["pending", "refund_failed"].includes(r.status) && (
            <div className="mt-1 flex gap-2">
              <button type="button" onClick={() => approve(r)} data-testid={`approve-refund-${r.id}`} className="px-2 py-1 border rounded">Approve refund</button>
              <button type="button" onClick={() => reject(r)} data-testid={`reject-refund-${r.id}`} className="px-2 py-1 border rounded">Reject refund</button>
              {r.status === "refund_failed" && <button type="button" onClick={() => markPaid(r)} data-testid={`mark-paid-refund-${r.id}`} className="px-2 py-1 border rounded">Mark paid</button>}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}

export function MyCoinPacks() {
  const [rows, setRows] = useState([]);
  const [rk, setRk] = useState(0);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  useEffect(() => {
    setState("loading");
    api.get("/coin-packs/mine")
      .then(({ data }) => { setRows(data || []); setState("ready"); })
      .catch((e) => { setError(err(e, "Couldn't load your coin packs.")); setState("error"); });
  }, [rk]);
  if (state === "loading") return <p data-testid="coin-packs-loading" className="text-xs">Loading coin packs…</p>;
  if (state === "error") return (
    <p data-testid="coin-packs-error" className="text-xs">{error}{" "}
      <button type="button" data-testid="coin-packs-retry" onClick={() => setRk((k) => k + 1)} className="underline">Retry</button></p>
  );
  if (!rows.length) return null;
  return (
    <div data-testid="my-coin-packs" className="text-xs">
      <p className="font-semibold">Coin pack purchases</p>
      {rows.map((o) => (
        <div key={o.order_id} className="mt-1 flex items-center gap-2">
          <span className="flex-1">{o.credits} coins · {o.created_at ? new Date(o.created_at).toLocaleDateString("en-IN") : ""}
            {o.refunded ? " · refunded" : ` · unused value ${money(o.refundable_minor)}`}</span>
          {!o.refunded && o.refundable_minor > 0 && <RequestRefundButton kind="coin_pack" refId={o.order_id} onDone={() => setRk((k) => k + 1)} />}
        </div>
      ))}
      <MyRefundRequests reloadKey={rk} />
    </div>
  );
}
